// Audio player: sequential playback built from manual #score-dropdown <li> items.
// Features:
//  - Duration display after metadata loads.
//  - Per-track play/pause toggle button (accessible + keyboard).
// Relies solely on DOM (no manifest fetch).

// window.__DEBUG_SLIDESHOW = window.__DEBUG_AUDIO = true;

// window.__DEBUG_OVERLAY = true; window.enableDebugOverlay && window.enableDebugOverlay();
// window.__DEBUG_SLIDESHOW = window.__DEBUG_AUDIO = true;

import lottie from 'lottie-web';

const audioPlayer = {
  tracks: [], // { index, number, file, audio, li }
  currentIndex: -1,
  initialized: false,
  _userPausedBgAudio: false,
  init() {
    if (this.initialized) return;
    this.initialized = true;
    this.$scoreDropdown = document.getElementById('score-dropdown');
    if (!this.$scoreDropdown) return;
    this.$list = this.$scoreDropdown.querySelector('ul');
    this.$waveform = document.querySelector('.nav-score-wrapper .waveform');
    this.$lottiePlayer = this.$waveform ? this.$waveform.querySelector('.lottie-player') : null;
    if (this.$lottiePlayer) {
      this.lottieInstance = lottie.loadAnimation({
        container: this.$lottiePlayer,
        renderer: 'svg',
        loop: false,
        autoplay: true,
        path: '/lottie/WaveOff.json',
      });
    }
    this.buildFromDOM();
    // If no tracks present in DOM, attempt to fetch a generated manifest and build the list
    // This enables auto-population from /public/audio/*.mp3 via a prebuild script.
    const maybeBuildFromManifest = async () => {
      if (this.tracks.length) return;
      try {
        // Fetch manifest and optional user metadata (for composer/title overrides)
        const [resManifest, resMeta] = await Promise.all([
          fetch('/audio-tracks.json', { cache: 'no-store' }).catch(() => null),
          fetch('/audio-metadata.json', { cache: 'no-store' }).catch(() => null)
        ]);
        if (!resManifest || !resManifest.ok) return;
        const items = await resManifest.json();
        let meta = null;
        if (resMeta && resMeta.ok) {
          try { meta = await resMeta.json(); } catch (_) { meta = null; }
        }
        const getMetaFor = (file, number) => {
          if (!meta) return {};
          // Support object map: { "01_-_Title.mp3": { composer, title } }
          if (!Array.isArray(meta) && typeof meta === 'object') {
            const m = meta[file] || meta[String(number)] || null;
            return m && typeof m === 'object' ? m : {};
          }
          // Support array of objects: [ { file, number, composer, title } ]
          if (Array.isArray(meta)) {
            const found = meta.find((m) => (m && (m.file === file || m.number === number)));
            return found || {};
          }
          return {};
        };
        if (!Array.isArray(items) || !items.length) return;
        // Inject list items into the dropdown
        const ul = this.$list || this.$scoreDropdown.querySelector('ul');
        if (!ul) return;
        items.forEach(({ number, file, title }) => {
          const overrides = getMetaFor(file, number);
          const titleText = overrides.title || title || file.replace(/\.mp3$/i, '');
          const composerText = overrides.composer || '';
          const li = document.createElement('li');
          li.setAttribute('data-track', String(number || ''));
          li.setAttribute('data-file', file);
          // structure expected by CSS
          const num = document.createElement('div');
          num.className = 'number';
          num.textContent = (number != null ? number : '').toString() + '.';
          const info = document.createElement('div');
          info.className = 'info';
          const t = document.createElement('div');
          t.className = 'track-title';
          t.textContent = titleText;
          const c = document.createElement('div');
          c.className = 'track-composer';
          c.textContent = composerText;
          info.appendChild(t);
          info.appendChild(c);
          const now = document.createElement('div');
          now.className = 'now-playing';
          now.textContent = 'NOW PLAYING';
          const time = document.createElement('div');
          time.className = 'time';
          li.appendChild(num);
          li.appendChild(info);
          li.appendChild(now);
          li.appendChild(time);
          ul.appendChild(li);
        });
        // Re-scan DOM now that items exist
        this.buildFromDOM();
      } catch (_) { /* ignore */ }
    };
    // Kick off manifest population if needed
    // Awaiting here would require init() to be async; instead, chain the rest of init after it completes
    // We'll proceed immediately, then rebind if tracks appear later.
    const manifestPromise = maybeBuildFromManifest();
    if (!this.tracks.length) {
      // If manifest fetch is pending, attempt to continue after it resolves
      if (manifestPromise && typeof manifestPromise.then === 'function') {
        manifestPromise.then(() => {
          if (!this.tracks.length) return;
          this._postBuildInit();
        });
      }
      // Exit early; _postBuildInit will handle the rest once tracks exist
      return;
    }
    this._postBuildInit();
  },

  _postBuildInit() {
    // Bind click + keyboard activation + add play buttons for built tracks
    // Bind click + keyboard activation + add play buttons
    this.tracks.forEach((track) => {
      const { li, index } = track;
      if (!li) return;
      // Insert play button if missing
      if (!li.querySelector('.play-toggle')) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'play-toggle';
        btn.setAttribute('aria-label', 'Play track');
        btn.textContent = '▶';
        // Place before number if number div exists else at start
        const numberEl = li.querySelector('.number');
        if (numberEl) {
          numberEl.parentNode.insertBefore(btn, numberEl);
        } else {
          li.insertAdjacentElement('afterbegin', btn);
        }
      }
      const playBtn = li.querySelector('.play-toggle');

      const liHandler = (e) => {
        if (e.type === 'keydown' && !(e.key === 'Enter' || e.key === ' ')) return;
        if (e.type === 'keydown') e.preventDefault();
        this.toggleTrack(index);
      };
      li.addEventListener('click', liHandler);
      li.setAttribute('tabindex', '0');
      li.addEventListener('keydown', liHandler);

      const btnHandler = (e) => {
        e.stopPropagation();
        this.toggleTrack(index);
      };
      playBtn.addEventListener('click', btnHandler);
      playBtn.addEventListener('keydown', (e) => {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          btnHandler(e);
        }
      });
    });

    // Waveform acts as a global play/pause toggle for current (or first) track
    if (this.$waveform) {
      const toggleFromWaveform = (e) => {
        e.preventDefault();
        if (this.currentIndex === -1) {
          // Start with first track
          this.playIndex(0);
          this._userPausedBgAudio = false;
          try { if (window.__DEBUG_AUDIO) console.debug('[AP] WF_PLAY_FIRST'); } catch(_) {}
        } else {
          // Toggle current track
          const t = this.tracks[this.currentIndex];
          if (!t) return;
          if (t.audio.paused) {
            t.audio.play().catch(()=>{});
            t.li && t.li.classList.add('playing');
            this._userPausedBgAudio = false;
            try { if (window.__DEBUG_AUDIO) console.debug(`[AP] WF_PLAY idx=${this.currentIndex}`); } catch(_) {}
          } else {
            t.audio.pause();
            t.li && t.li.classList.remove('playing');
            this._userPausedBgAudio = true;
            try { if (window.__DEBUG_AUDIO) console.debug(`[AP] WF_PAUSE idx=${this.currentIndex}`); } catch(_) {}
          }
          this.updateButtons();
        }
      };
      this.$waveform.setAttribute('role', 'button');
      this.$waveform.setAttribute('tabindex', '0');
      this.$waveform.setAttribute('aria-label', 'Play/Pause score audio');
      this.$waveform.addEventListener('click', toggleFromWaveform);
      this.$waveform.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleFromWaveform(e); }
      });
    }

    // Tab visibility pause/resume: remember if audio was actively playing and restore only in that case
    this._resumeOnVisibility = false;
    const handleVisibility = () => {
      if (document.hidden) {
        const idx = this.currentIndex;
        if (idx !== -1) {
          const t = this.tracks[idx];
          if (t && !t.audio.paused && !t.audio.ended) {
            this._resumeOnVisibility = true;
            try { t.audio.pause(); } catch(_){}
            t.li && t.li.classList.remove('playing');
            this.updateButtons();
            this.updateWaveformState();
          } else {
            this._resumeOnVisibility = false;
          }
        } else {
          this._resumeOnVisibility = false;
        }
      } else {
        if (this._resumeOnVisibility) {
          const idx = this.currentIndex;
          if (idx !== -1) {
            const t = this.tracks[idx];
            if (t && t.audio.paused && !t.audio.ended) {
              t.audio.play().catch(()=>{});
              t.li && t.li.classList.add('playing');
              this.updateButtons();
              this.updateWaveformState();
            }
          }
        }
        this._resumeOnVisibility = false; // one-shot resume
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    // Provide cleanup hook if needed later
    this._visibilityCleanup = () => document.removeEventListener('visibilitychange', handleVisibility);

    // Ensure initial waveform state renders correctly
    this.updateWaveformState();

    // On first user interaction anywhere, auto-play the first track
    if (!this._autoStartInstalled && this.tracks.length) {
      this._autoStartInstalled = true;
      const autoStart = () => {
        // Only start if nothing is playing yet
        if (this.currentIndex === -1) {
          this.playIndex(0);
        }
      };
      // Use pointerdown for broad input coverage; add keydown as fallback
      window.addEventListener('pointerdown', autoStart, { once: true });
      window.addEventListener('keydown', autoStart, { once: true });
    }
  },

  buildFromDOM() {
    const listItems = Array.from(this.$scoreDropdown.querySelectorAll('ul li'));
    if (!listItems.length) return;
    this.tracks = listItems.map((li, idx) => {
      const number = parseInt(li.getAttribute('data-track'), 10) || idx + 1;
      const fileAttr = li.getAttribute('data-file');
      const filename = fileAttr || `${number}.mp3`;
      const file = `./audio/${filename}`;
      const audio = new Audio(file);
      audio.preload = 'metadata';
      audio.addEventListener('ended', () => this.playNext());
      audio.addEventListener('loadedmetadata', () => this.updateDuration(li, audio));
      audio.addEventListener('error', () => {
        console.warn('[audioPlayer] Failed to load audio src:', file);
      });
      audio.addEventListener('play', () => {
        // ensure correct li has playing class
        if (this.currentIndex === idx) {
          li.classList.add('playing');
          this.updateButtons();
          this.updateWaveformState();
        }
      });
      audio.addEventListener('pause', () => {
        if (this.currentIndex === idx) {
          li.classList.remove('playing');
          this.updateButtons();
          this.updateWaveformState();
        }
      });
      return { index: idx, number, file, audio, li };
    });
  },

  clearPlayingClass() {
    this.tracks.forEach(t => t.li && t.li.classList.remove('playing'));
  },

  stopCurrent() {
    if (this.currentIndex === -1) return;
    const cur = this.tracks[this.currentIndex];
    try {
      cur.audio.pause();
    } catch (e) { /* ignore */ }
  },

  playIndex(idx) {
    if (idx < 0 || idx >= this.tracks.length) return;
    // Stop current
    this.stopCurrent();
    this.currentIndex = idx;
    const track = this.tracks[idx];
    this.clearPlayingClass();
    track.li && track.li.classList.add('playing');
    track.audio.play().catch(err => {
      console.warn('[audioPlayer] Playback error:', err.message);
    });
    this.updateButtons();
    this.updateWaveformState();
    this._userPausedBgAudio = false;
  },

  playNext() {
    const next = this.currentIndex + 1;
    if (next < this.tracks.length) {
      this.playIndex(next);
    } else {
      this.clearPlayingClass();
      this.currentIndex = -1;
      this.updateButtons();
      this.updateWaveformState();
    }
  }
};

// Additional helper methods appended after object definition
audioPlayer.updateDuration = function(li, audio) {
  if (!li) return;
  const timeEl = li.querySelector('.time');
  if (!timeEl) return;
  if (!isFinite(audio.duration)) return;
  timeEl.textContent = this.formatTime(audio.duration);
};

audioPlayer.formatTime = function(totalSeconds) {
  const s = Math.floor(totalSeconds % 60).toString().padStart(2, '0');
  const m = Math.floor(totalSeconds / 60).toString();
  return `${m}:${s}`;
};

audioPlayer.toggleTrack = function(idx) {
  if (this.currentIndex === idx) {
    const track = this.tracks[idx];
    if (!track) return;
    if (track.audio.paused) {
      track.audio.play().catch(()=>{});
      track.li && track.li.classList.add('playing');
      this._userPausedBgAudio = false;
    } else {
      track.audio.pause();
      track.li && track.li.classList.remove('playing');
      this._userPausedBgAudio = true;
    }
    this.updateButtons();
    this.updateWaveformState();
  } else {
    this.playIndex(idx);
  }
};

audioPlayer.updateButtons = function() {
  this.tracks.forEach((t, idx) => {
    const btn = t.li && t.li.querySelector('.play-toggle');
    if (!btn) return;
    const isCurrent = idx === this.currentIndex;
    const isPlaying = isCurrent && !t.audio.paused && !t.audio.ended;
    btn.textContent = isPlaying ? '❚❚' : '▶';
    btn.setAttribute('aria-label', (isPlaying ? 'Pause' : 'Play') + ' track ' + (t.number));
  });
};

audioPlayer.updateWaveformState = function() {
  if (!this.$waveform) return;
  // Determine playing based on currentIndex and paused state
  const playing = this.currentIndex !== -1 && !this.tracks[this.currentIndex].audio.paused;
  this.$waveform.classList.toggle('playing', playing);
  this.$waveform.setAttribute('aria-pressed', playing ? 'true' : 'false');

  // Lottie animation control
  if (this.$lottiePlayer && this.lottieInstance) {
    if (playing) {
      // Load and loop WaveLoop_opt.json
      this.lottieInstance.destroy();
      this.lottieInstance = lottie.loadAnimation({
        container: this.$lottiePlayer,
        renderer: 'svg',
        loop: true,
        autoplay: true,
        path: '/lottie/WaveLoop-2.json',
      });
    } else {
      // Load and play WaveOff.json once
      this.lottieInstance.destroy();
      this.lottieInstance = lottie.loadAnimation({
        container: this.$lottiePlayer,
        renderer: 'svg',
        loop: false,
        autoplay: true,
        path: '/lottie/WaveOff.json',
      });
    }
  }
};

// Pause/resume hooks for slideshow video playback
audioPlayer.pauseForSlideshow = function() {
  try {
    // Mark intent to resume score after slideshow regardless of current state.
    // If a track is actively playing, pause it; otherwise just set the resume flag.
    this._resumeFromSlideshow = true;
    const idx = this.currentIndex;
    if (idx !== -1) {
      const t = this.tracks[idx];
      if (t && !t.audio.paused && !t.audio.ended) {
        try { t.audio.pause(); } catch(_) {}
        t.li && t.li.classList.remove('playing');
        this.updateButtons();
        this.updateWaveformState();
      }
    }
  } catch(_) {}
};

audioPlayer.resumeFromSlideshow = function() {
  try {
    if (!this._resumeFromSlideshow) return;
    try { if (window.__DEBUG_AUDIO) console.debug(`[AP] RESUME_FROM_SS userPaused=${this._userPausedBgAudio ? 1 : 0} cur=${this.currentIndex}`); } catch(_) {}
    // Respect explicit user pause of the background score
    if (this._userPausedBgAudio) { this._resumeFromSlideshow = false; return; }
    let idx = this.currentIndex;
    // If no current track selected yet, start with the first track
    if (idx === -1) {
      this.playIndex(0);
      this._resumeFromSlideshow = false;
      try { if (window.__DEBUG_AUDIO) console.debug('[AP] RESUME_START_FIRST'); } catch(_) {}
      return;
    }
    const t = this.tracks[idx];
    if (!t || t.audio.ended) { this._resumeFromSlideshow = false; return; }
    if (t.audio.paused) {
      t.audio.play().catch(()=>{});
      t.li && t.li.classList.add('playing');
      this.updateButtons();
      this.updateWaveformState();
      try { if (window.__DEBUG_AUDIO) console.debug(`[AP] RESUMED idx=${idx}`); } catch(_) {}
    }
    this._resumeFromSlideshow = false;
  } catch(_) {}
};

export default audioPlayer;